import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Send, X, User, Headphones } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { toast } from "sonner";

export default function ClaimChat({ claim, embedded = false }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const isManager = ["claim_manager", "admin"].includes(user?.role);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(embedded);
  const endRef = useRef(null);
  const lastPoll = useRef(null);

  const load = async () => {
    try {
      const r = await api.get(`/chat/${claim.claim_id}/messages${lastPoll.current ? `?since=${encodeURIComponent(lastPoll.current)}` : ""}`);
      if (r.data.length > 0) {
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.message_id));
          const merged = [...prev, ...r.data.filter((m) => !seen.has(m.message_id))];
          return merged;
        });
        lastPoll.current = r.data[r.data.length - 1].at;
      }
    } catch { /* silent poll */ }
  };

  const loadUnread = async () => {
    if (open) return;
    try {
      const r = await api.get(`/chat/${claim.claim_id}/unread`);
      setUnread(r.data.unread || 0);
    } catch { /* silent */ }
  };

  const markRead = async () => {
    try { await api.post(`/chat/${claim.claim_id}/read`); setUnread(0); } catch { /* silent */ }
  };

  useEffect(() => {
    (async () => {
      // Initial full load
      const r = await api.get(`/chat/${claim.claim_id}/messages`).catch(() => ({ data: [] }));
      setMessages(r.data);
      if (r.data.length > 0) lastPoll.current = r.data[r.data.length - 1].at;
      loadUnread();
    })();
    const t1 = setInterval(load, 5000);
    const t2 = setInterval(loadUnread, 8000);
    return () => { clearInterval(t1); clearInterval(t2); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [claim.claim_id]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    if (open) markRead();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const send = async () => {
    const body = input.trim();
    if (!body || sending) return;
    setSending(true);
    try {
      const r = await api.post(`/chat/${claim.claim_id}/messages`, { body });
      setMessages((prev) => [...prev, r.data]);
      lastPoll.current = r.data.at;
      setInput("");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setSending(false);
    }
  };

  const Panel = (
    <div className="flex flex-col h-full bg-white dark:bg-slate-900" data-testid="claim-chat">
      {!embedded && (
        <div className="px-4 py-3 bg-gradient-to-r from-[#003DA5] to-[#0055D4] text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Headphones className="w-4 h-4" />
            <div className="font-heading font-bold text-sm">{t("chat.title")}</div>
          </div>
          <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/15" aria-label="Close" data-testid="chat-close">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      {embedded && (
        <div className="px-4 py-3 border-b border-slate-100 dark:border-slate-800 flex items-center gap-2">
          <MessageCircle className="w-4 h-4 text-[#003DA5]" />
          <div className="font-heading font-bold text-slate-900 dark:text-white text-sm">{t("chat.title")}</div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 min-h-[280px] max-h-[420px]">
        {messages.length === 0 ? (
          <div className="text-center text-sm text-slate-500 dark:text-slate-400 py-10" data-testid="chat-empty">{t("chat.empty")}</div>
        ) : (
          messages.map((m) => {
            const mine = m.sender_id === user?.user_id;
            const bubbleTone = mine
              ? "bg-[#003DA5] text-white rounded-2xl rounded-br-md"
              : m.sender_role === "manager"
                ? "bg-[#00A86B]/10 dark:bg-[#00A86B]/20 text-slate-900 dark:text-white rounded-2xl rounded-bl-md border border-[#00A86B]/20"
                : "bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-white rounded-2xl rounded-bl-md";
            return (
              <motion.div
                key={m.message_id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex ${mine ? "justify-end" : "justify-start"} gap-2`}
                data-testid={`chat-msg-${m.message_id}`}
              >
                {!mine && (
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${m.sender_role === "manager" ? "bg-[#00A86B] text-white" : "bg-slate-300 dark:bg-slate-700 text-slate-700 dark:text-slate-300"}`}>
                    {m.sender_role === "manager" ? <Headphones className="w-3 h-3" /> : <User className="w-3 h-3" />}
                  </div>
                )}
                <div className="max-w-[75%]">
                  <div className={`px-3 py-2 text-sm ${bubbleTone}`}>{m.body}</div>
                  <div className={`text-[10px] text-slate-400 mt-0.5 ${mine ? "text-right" : ""}`}>
                    {mine ? "You" : m.sender_name} · {new Date(m.at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
              </motion.div>
            );
          })
        )}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-200 dark:border-slate-800 flex items-center gap-2">
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("chat.placeholder")}
          className="flex-1 px-3 py-2.5 rounded-full bg-slate-100 dark:bg-slate-800 text-sm text-slate-900 dark:text-white focus:bg-white dark:focus:bg-slate-900 focus:ring-2 focus:ring-[#003DA5] outline-none transition-all"
          disabled={sending}
          data-testid="chat-input"
        />
        <button
          type="submit"
          disabled={!input.trim() || sending}
          className="w-10 h-10 rounded-full bg-[#003DA5] hover:bg-[#002D7A] text-white flex items-center justify-center shrink-0 disabled:opacity-40"
          aria-label={t("chat.send")}
          data-testid="chat-send"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>
    </div>
  );

  if (embedded) {
    return (
      <div className="rounded-2xl border border-slate-200 dark:border-slate-800 overflow-hidden">
        {Panel}
      </div>
    );
  }

  // Floating chat bubble variant
  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-24 right-6 z-40 w-14 h-14 rounded-full bg-[#003DA5] text-white shadow-2xl flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Open chat"
        data-testid="chat-fab"
      >
        <MessageCircle className="w-6 h-6" />
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 rounded-full bg-[#FF7A00] text-white text-[10px] font-bold flex items-center justify-center px-1" data-testid="chat-unread">{unread}</span>
        )}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 24, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.95 }}
            className="fixed bottom-24 right-6 z-40 w-[min(380px,calc(100vw-2rem))] max-h-[70vh] rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-white dark:bg-slate-900"
          >
            {Panel}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
